import { useEffect, useRef, useState } from "react"

export default function useStoryblok({ story: originalStory, resolve_relations = false }) {
  const [story, setStory] = useState(() => normalizeEditableStory(originalStory))
  const storyRef = useRef(story)
  const bridgeInitialized = useRef(false)

  useEffect(() => {
    const normalizedStory = normalizeEditableStory(originalStory)

    storyRef.current = normalizedStory
    setStory(normalizedStory)
  }, [originalStory])

  useEffect(() => {
    //  bail if we're not in a Storyblok preview iframe
    if (
      typeof window === "undefined" ||
      !window.location.search.includes("_storyblok") ||
      bridgeInitialized.current
    ) {
      return
    }

    const initBridge = () => {
      if (!window.StoryblokBridge || bridgeInitialized.current) {
        return
      }

      bridgeInitialized.current = true
      const bridge = initEventListeners(resolve_relations, storyRef, setStory)

      requestEditorScan(bridge)
    }

    if (window.StoryblokBridge) {
      initBridge()
      return
    }

    const existingScript = document.querySelector("#storyblokBridge")

    if (existingScript) {
      existingScript.addEventListener("load", initBridge)
      return () => existingScript.removeEventListener("load", initBridge)
    } else {
      const script = document.createElement("script")
      script.src = "https://app.storyblok.com/f/storyblok-v2-latest.js"
      script.id = "storyblokBridge"
      script.addEventListener("load", initBridge)
      document.body.appendChild(script)

      return () => script.removeEventListener("load", initBridge)
    }
  }, [originalStory, resolve_relations])

  return story
}

/**
 *
 * Set up the Storyblok Bridge interactive editor
 *
 * @returns undefined
 */
function initEventListeners(resolveRelations, storyRef, setStory) {
  const { StoryblokBridge } = window

  resolveRelations = normalizeResolveRelations(resolveRelations)

  const updateStory = (story) => {
    const normalizedStory = normalizeEditableStory(story)

    storyRef.current = normalizedStory
    setStory(normalizedStory)
  }

  const bridge = new StoryblokBridge({
    resolveRelations,
    resolveLinks: "url",
  })

  // The preview route handles the initial Storyblok render. Avoid an extra client-side
  // draft API fetch here, because published-only tokens return 401 for version=draft.
  bridge.on("enterEditmode", (event) => {
    if (event.story) {
      updateStory(event.story)
    }
  })

  /**
   * live update the story on input events
   *
   * - check if the ids of the event and the passed story match
   * - change the story content through the setStory function
   **/
  bridge.on("input", (event) => {
    const nextStory = event.story
    const currentStory = storyRef.current

    if (!currentStory?.content?._uid || nextStory?.content?._uid === currentStory.content._uid) {
      updateStory(nextStory)
    }
  })

  // reload Next.js page on save or publish event in the Visual Editor
  bridge.on(["published", "change"], () => location.reload(true))

  return bridge
}

function requestEditorScan(bridge, attempt = 0) {
  window.requestAnimationFrame(() => {
    if (bridge?.pingEditor) {
      bridge.pingEditor(() => {})
      return
    }

    if (attempt < 5) {
      window.setTimeout(() => requestEditorScan(bridge, attempt + 1), 100)
    }
  })
}

function normalizeEditableStory(story) {
  const editableOptions = getEditableOptions(story?.content?._editable)

  if (!editableOptions?.id || !editableOptions?.space) {
    return story
  }

  return {
    ...story,
    content: addMissingEditableData(story.content, editableOptions),
  }
}

function addMissingEditableData(value, storyEditableOptions) {
  if (Array.isArray(value)) {
    return value.map((item) => addMissingEditableData(item, storyEditableOptions))
  }

  if (!value || typeof value !== "object") {
    return value
  }

  const nextValue = Object.fromEntries(
    Object.entries(value).map(([key, nestedValue]) => [key, addMissingEditableData(nestedValue, storyEditableOptions)]),
  )

  if (nextValue._uid && nextValue.component && !nextValue._editable) {
    nextValue._editable = makeEditableComment({
      id: storyEditableOptions.id,
      space: storyEditableOptions.space,
      uid: nextValue._uid,
      name: nextValue.component,
    })
  }

  return nextValue
}

function makeEditableComment(options) {
  return `<!--#storyblok#${JSON.stringify(options)}-->`
}

function getEditableOptions(editable) {
  if (typeof editable === "object") {
    return editable
  }

  if (typeof editable !== "string") {
    return null
  }

  try {
    return JSON.parse(editable.replace(/^<!--#storyblok#/, "").replace(/-->$/, ""))
  } catch {
    return null
  }
}

function normalizeResolveRelations(resolveRelations) {
  if (Array.isArray(resolveRelations)) {
    return resolveRelations
  }

  if (typeof resolveRelations !== "string") {
    return []
  }

  return resolveRelations
    .split(",")
    .map((relation) => relation.trim())
    .filter(Boolean)
}
